import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Rate Card Roles ============

export interface RateCardRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function useRateCardRoles(includeInactive = false) {
  return useQuery({
    queryKey: ['rate-card-roles', { includeInactive }],
    queryFn: () =>
      api.get<{ roles: RateCardRole[] }>('/rate-cards/roles', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateRateCardRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { code: string; name: string; description?: string; sortOrder?: number }) =>
      api.post<{ role: RateCardRole }>('/rate-cards/roles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-roles'] });
    },
  });
}

export function useUpdateRateCardRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; code?: string; name?: string; description?: string | null; sortOrder?: number; isActive?: boolean }) =>
      api.patch<{ role: RateCardRole }>(`/rate-cards/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-roles'] });
    },
  });
}

export function useDeleteRateCardRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/rate-cards/roles/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-roles'] });
    },
  });
}

// ============ Rate Card Types ============

export interface RateCardType {
  id: string;
  code: string;
  name: string;
  description: string | null;
  defaultCurrency: 'GBP' | 'USD' | 'EUR';
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RateCardTypeColumn {
  id: string;
  typeId: string;
  code: string;
  label: string;
  rateUnit: 'hourly' | 'daily';
  isCustomerFacing: boolean;
  isXtravirtRate: boolean;
  isInternalCost: boolean;
  sortOrder: number;
  createdAt: string;
}

export interface RateCardTypeWithColumns extends RateCardType {
  columns: RateCardTypeColumn[];
}

// List rate card types
export function useRateCardTypes(includeInactive = false) {
  return useQuery({
    queryKey: ['rate-card-types', { includeInactive }],
    queryFn: () =>
      api.get<{ types: RateCardType[] }>('/rate-cards/types', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
  });
}

// Get single rate card type with columns
export function useRateCardType(id: string) {
  return useQuery({
    queryKey: ['rate-card-types', id],
    queryFn: () =>
      api.get<{ type: RateCardTypeWithColumns }>(`/rate-cards/types/${id}`),
    enabled: !!id,
  });
}

// Create rate card type
export function useCreateRateCardType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      code: string;
      name: string;
      description?: string;
      columns: Array<{
        code: string;
        label: string;
        rateUnit?: 'hourly' | 'daily';
        isCustomerFacing?: boolean;
        isXtravirtRate?: boolean;
        isInternalCost?: boolean;
      }>;
    }) => api.post<{ type: RateCardTypeWithColumns }>('/rate-cards/types', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-types'] });
    },
  });
}

// Update rate card type
export function useUpdateRateCardType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      description?: string;
      sortOrder?: number;
      isActive?: boolean;
    }) => api.patch<{ type: RateCardType }>(`/rate-cards/types/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-types'] });
      queryClient.invalidateQueries({ queryKey: ['rate-card-types', variables.id] });
    },
  });
}

// Delete rate card type (soft delete)
export function useDeleteRateCardType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/rate-cards/types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-card-types'] });
    },
  });
}

// ============ Rate Cards ============

export interface RateCard {
  id: string;
  typeId: string | null;
  name: string;
  currency: 'GBP' | 'USD' | 'EUR';
  hoursPerDay: number;
  expensesDayRate: number;
  exchangeRate: number | null;
  isDefault: boolean;
  isActive: boolean;
  validFrom: string | null;
  validTo: string | null;
  createdAt: string;
  updatedAt: string;
  // Expanded type info when included
  type?: RateCardType;
}

export interface RateCardEntry {
  id: string;
  rateCardId: string;
  roleId: string | null;
  role: string;
  // Legacy columns (for backwards compatibility)
  xtravirtDailyGbp: number;
  t6HourlyEntitlement: number | null;
  t1t4HourlyCash: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RateCardEntryValue {
  id: string;
  entryId: string;
  columnId: string;
  value: number;
}

export interface RateCardEntryWithValues extends RateCardEntry {
  values: RateCardEntryValue[];
}

// List rate cards
export function useRateCards(includeInactive = false) {
  return useQuery({
    queryKey: ['rate-cards', { includeInactive }],
    queryFn: () =>
      api.get<{ rateCards: RateCard[] }>('/rate-cards', {
        includeInactive: includeInactive ? 'true' : undefined,
      }),
  });
}

// Get single rate card with entries and values
export function useRateCard(id: string) {
  return useQuery({
    queryKey: ['rate-cards', id],
    queryFn: () =>
      api.get<{
        rateCard: RateCard;
        entries: RateCardEntryWithValues[];
        type?: RateCardTypeWithColumns;
      }>(`/rate-cards/${id}`),
    enabled: !!id,
  });
}

// Create rate card
export function useCreateRateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      name: string;
      typeId?: string;
      currency?: 'GBP' | 'USD' | 'EUR';
      hoursPerDay?: number;
      expensesDayRate?: number;
      isDefault?: boolean;
      validFrom?: string;
      validTo?: string;
    }) => api.post<{ rateCard: RateCard }>('/rate-cards', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-cards'] });
    },
  });
}

// Update rate card
export function useUpdateRateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      name?: string;
      typeId?: string;
      currency?: 'GBP' | 'USD' | 'EUR';
      hoursPerDay?: number;
      expensesDayRate?: number;
      isDefault?: boolean;
      isActive?: boolean;
      validFrom?: string | null;
      validTo?: string | null;
    }) => api.patch<{ rateCard: RateCard }>(`/rate-cards/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rate-cards'] });
      queryClient.invalidateQueries({ queryKey: ['rate-cards', variables.id] });
    },
  });
}

// Set rate card entries with dynamic values
export function useSetRateCardEntries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      rateCardId,
      entries,
    }: {
      rateCardId: string;
      entries: Array<{
        roleId?: string;
        role: string;
        values?: Array<{
          columnId: string;
          value: number;
        }>;
        // Legacy fields (for backwards compatibility)
        xtravirtDailyGBP?: number;
        t6HourlyEntitlement?: number;
        t1t4HourlyCash?: number;
      }>;
    }) => api.put<{ entries: RateCardEntryWithValues[] }>(`/rate-cards/${rateCardId}/entries`, { entries }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['rate-cards', variables.rateCardId] });
    },
  });
}

// Delete rate card
export function useDeleteRateCard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/rate-cards/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rate-cards'] });
    },
  });
}
